// dependencies
import React, { useMemo } from "react";

// components
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons";

// py-components
import { ICellRendererParams, Icon, PYColumnDef, SortChangedEvent, Table, Text } from "@performyard/py-components";

// hooks
import { SearchServiceSort } from "../../../../../../api/hooks";

import { Goal } from "../../../../../../api/services/searchServiceResultTypes/goal";

import get from "../../../../../../utils/GetNested";

interface BaseProps {
	goals: Goal[];
	isFetchingGoals: boolean;
	handleColumnHeaderClick: (columnId: SearchServiceSort) => void;
	activeSort: string;
	activeSortDirection: string;
	onRowClick: (goal: Goal) => void;
}

// Custom cell renderer for goal name with click handler
const GoalNameCellRenderer = (params: ICellRendererParams<Goal>) => {
	const goal = params.data;
	const context = params.context;

	if (!goal) return null;

	const goalName = get(goal, "name", "");

	return (
		<div
			data-testid="name"
			className="flex h-full w-full min-w-0 max-w-full cursor-pointer items-center justify-start"
			onClick={() => context?.onRowClick?.(goal)}
		>
			<Text size="paragraph-3" color="primary-5" dangerouslySetClassName="line-clamp-1">
				{goalName}
			</Text>
		</div>
	);
};

// Custom cell renderer for visibility indicator
const VisibilityCellRenderer = (params: ICellRendererParams<Goal>) => {
	const goal = params.data;
	if (!goal) return null;

	const isPrivateGoal = get(goal, "transparency", "") === "private";

	return (
		<div data-testid="visibilityContainer" className="flex h-full items-center justify-center">
			<Icon icon={faEyeSlash} color={isPrivateGoal ? "primary-5" : "neutral-3"} />
		</div>
	);
};

// Column definitions for lineage table
const createColumnDefs = (): PYColumnDef<Goal>[] => {
	return [
		{
			headerName: "Name",
			field: "name",
			cellRenderer: GoalNameCellRenderer,
			width: 342,
			sortable: true,
			resizable: false,
		},
		{
			headerName: "",
			field: "transparency",
			cellRenderer: VisibilityCellRenderer,
			width: 24,
			sortable: false,
			resizable: false,
		},
		{
			headerName: "Assignee",
			field: "assignee.name",
			type: "employee",
			width: 150,
			sortable: true,
			resizable: false,
			valueGetter: (params) => params.data?.assignee,
		},
		{
			headerName: "Due Date",
			field: "end_date",
			type: "date",
			width: 116,
			sortable: true,
			resizable: false,
			cellRendererParams: {
				dateFormat: "MMM d, yyyy",
			},
			valueGetter: (params) => params.data?.endDate,
		},
		{
			headerName: "Status",
			field: "status",
			type: "text",
			width: 72,
			sortable: false,
			resizable: false,
			valueFormatter: (params) => {
				const status = params.value === "done" ? "completed" : params.value;
				return status ? status.charAt(0).toUpperCase() + status.slice(1) : "";
			},
		},
		{
			headerName: "Supporting Goals",
			field: "descendantsCount",
			type: "text",
			width: 154,
			sortable: false,
			resizable: false,
			valueGetter: (params) => params.data?.descendantsCount ?? "",
		},
		{
			headerName: "Progress",
			field: "progress",
			type: "progress",
			width: 224,
			sortable: true,
			resizable: false,
			valueGetter: (params) => {
				const goal = params.data;
				if (!goal) return null;
				return get(goal, "progress", 0) * 100;
			},
			cellRendererParams: (goal: Goal) => ({
				colorOverride: get(goal, ["outlook", "color"], undefined),
				ariaLabel: "Goal Progress",
			}),
		},
	];
};

const GoalLineageTable = (props: BaseProps) => {
	const { goals, isFetchingGoals, handleColumnHeaderClick, activeSort, activeSortDirection, onRowClick } = props;

	// Memoize column definitions to prevent recreation on every render
	const columnDefs = useMemo(() => createColumnDefs(), []);

	// Handle sort changes from AG Grid
	const onSortChanged = (event: SortChangedEvent<Goal>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn && sortedColumn.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<div data-testid="goalLineageTableContainer">
			<Table<Goal>
				autoHeight
				columnSizing="fit-grid"
				rowData={goals}
				columnDefs={columnDefs}
				loading={isFetchingGoals}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText={"No Aligned Goals Found"}
				// Set initial sort state from props
				initialState={{
					sort: {
						sortModel: activeSort
							? [
									{
										colId: activeSort,
										sort: activeSortDirection as "asc" | "desc",
									},
								]
							: [],
					},
				}}
				animateRows={false}
				context={{ onRowClick }}
			/>
		</div>
	);
};

export default GoalLineageTable;
